import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Toolbar from "@mui/material/Toolbar";
import { Link } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { getNameBusinessTheme } from "../../actions/getters";
import { DRAWER_WIDTH, ROLES_ALLOWED } from "../../utils/constants";
import { useRebrandBanner } from "../../hooks/useRebrandBanner";
import { useMediaQuery } from "@mui/material";
import { setOpenSlideMenu } from "../../actions/ui";
import defaultMenus from "../../data/listMenus.js";
import MenuGroupItem from "./MenuGroupItem.jsx";
import { BUY_CREDITS } from "../../i18n/keysTranslations.js";
import { useTranslationApp } from "../../lib/i18next.jsx";
import Button from "../Buttons/Button.jsx";
import ButtonCreateAccountFromAnonymous from "../Buttons/ButtonCreateAccountFromAnonymous.jsx";
import { SelectAdvertiserID } from "../Form/SelectAdvertiserID.jsx";
import OnboardingSetupProgress from "../Onboarding/OnboardingSetupProgress.jsx";

function useSlideMenuDrawer({
  isTablet,
  mobileDrawerOpen,
  onMobileDrawerClose,
  slideMenuIsOpen,
  dispatch,
}) {
  const isMobileDrawerControlled = useMemo(() => {
    return (
      mobileDrawerOpen !== undefined &&
      typeof onMobileDrawerClose === "function"
    );
  }, [mobileDrawerOpen, onMobileDrawerClose]);

  const variant = isTablet ? "temporary" : "permanent";

  const open = useMemo(() => {
    if (!isTablet) return true;
    return isMobileDrawerControlled ? mobileDrawerOpen : slideMenuIsOpen;
  }, [isTablet, isMobileDrawerControlled, mobileDrawerOpen, slideMenuIsOpen]);

  const onDrawerClose = useCallback(() => {
    if (isMobileDrawerControlled) {
      onMobileDrawerClose();
      return;
    }
    dispatch(setOpenSlideMenu(!slideMenuIsOpen));
  }, [
    isMobileDrawerControlled,
    onMobileDrawerClose,
    slideMenuIsOpen,
    dispatch,
  ]);

  const onTabletLinkAfterNav = isMobileDrawerControlled
    ? onMobileDrawerClose
    : undefined;

  return {
    variant,
    open,
    onDrawerClose,
    onTabletLinkAfterNav,
  };
}

const SlideMenu = ({
  onClickBuyCredits,
  showBuyCreditsButton,
  menus = defaultMenus,
  mobileDrawerOpen,
  onMobileDrawerClose,
  hideMobileFooter = false,
}) => {
  const dispatch = useDispatchApp();
  const theme = useSelectorApp((state) => state.ui.theme);
  const { slideMenuIsOpen } = useSelectorApp((state) => state.ui);
  const { user } = useSelectorApp((state) => state.auth);
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());

  const { t } = useTranslationApp();
  const isAnonymous = user?.isAnonymous;
  const [showAdvertiserSelector, setShowAdvertiserSelector] = useState(false);

  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const { variant, open, onDrawerClose, onTabletLinkAfterNav } =
    useSlideMenuDrawer({
      isTablet,
      mobileDrawerOpen,
      onMobileDrawerClose,
      slideMenuIsOpen,
      dispatch,
    });
  const { showBanner } = useRebrandBanner();

  const { mainMenus, bottomSubMenus } = useMemo(() => {
    const bottomSubMenus = [];
    const mainMenus = menus.map((menu) => ({
      ...menu,
      subMenus: menu.subMenus.filter((subMenu) => {
        if (subMenu.isPinnedBottom) {
          bottomSubMenus.push(subMenu);
          return false;
        }
        return true;
      }),
    }));
    return { mainMenus, bottomSubMenus };
  }, [menus]);

  return (
    <Drawer
      open={open}
      variant={variant}
      onClose={onDrawerClose}
      anchor="left"
      data-tour="navigation"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          backgroundColor: "#ffffff",
          borderRight: "1px solid #e5e5e5 !important",
          color: "background.paperContrastText",
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          "&.MuiPaper-root": {
            overflowX: "hidden",
            borderRight: "none",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Toolbar
          sx={{
            pt: showBanner ? 5.5 : 0,
            pb: showBanner ? 1 : 0,
            mt: { xs: 1, md: 0 },
            mb: { xs: 0.5, md: 0 },
            display: "flex",
            justifyContent: "flex-start",
            overflowX: "hidden",
            overflow: "hidden",
          }}
        >
          <Link
            to="/"
            style={{
              display: "flex",
              width: "65%",
              justifyContent: "flex-start",
            }}
          >
            <Avatar
              src={theme.logo}
              alt={`Logo ${NAME_BUSINESS}`}
              variant="square"
              sx={{
                width: "60px",
                mt: 1,
                ".MuiAvatar-img": {
                  maxWidth: "105px",
                  objectFit: "contain",
                },
              }}
            />
          </Link>
        </Toolbar>
        <OnboardingSetupProgress onNavigate={onTabletLinkAfterNav} />
        <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {mainMenus.map((menu) => (
            <MenuGroupItem
              key={menu.nameKey}
              menu={menu}
              onTabletLinkClick={onTabletLinkAfterNav}
            />
          ))}
        </Box>
        <Box sx={{ flexShrink: 0, mt: "auto" }}>
          {!hideMobileFooter && (
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                flexDirection: "column",
                width: "100%",
                alignItems: "center",
                gap: 2,
                pb: 1,
              }}
            >
              {showBuyCreditsButton && (
                <Box
                  sx={{
                    width: "100%",
                    px: 2,
                  }}
                >
                  <Button
                    color="secondary"
                    hasShadow={false}
                    fullWidth
                    sx={{
                      height: 40,
                      maxHeight: 40,
                    }}
                    onClick={onClickBuyCredits}
                  >
                    {t(BUY_CREDITS)}
                  </Button>
                </Box>
              )}
              <Box
                sx={{
                  width: "100%",
                  px: 2,
                  display: showAdvertiserSelector ? "block" : "none",
                }}
              >
                <SelectAdvertiserID
                  fullWidth
                  onLoadComplete={(willBeVisible) =>
                    setShowAdvertiserSelector(willBeVisible)
                  }
                />
              </Box>
              {isAnonymous ? (
                <Box
                  sx={{
                    width: "100%",
                    px: 2,
                  }}
                >
                  <ButtonCreateAccountFromAnonymous
                    color="secondary"
                    hasShadow={false}
                    fullWidth
                    sx={{
                      height: 40,
                      maxHeight: 40,
                    }}
                  />
                </Box>
              ) : null}
            </Box>
          )}
          {bottomSubMenus.length > 0 ? (
            <MenuGroupItem
              key="bottom"
              menu={{
                nameKey: "bottom",
                roles: ROLES_ALLOWED,
                subMenus: bottomSubMenus,
              }}
              onTabletLinkClick={onTabletLinkAfterNav}
            />
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
};

export default SlideMenu;
